/**
 * AstroSong Natal Chart Calculator v2.0
 *
 * Powered by Astronomy Engine (cosinekitty) for high-precision positions.
 * Accuracy: ±1 arcminute (vs ±1-2° in v1.0).
 * Includes Placidus house system calculation.
 *
 * Requires: astronomy.browser.min.js loaded before this file.
 *
 * Usage (unchanged from v1.0):
 *   const chart = AstroCalc.calculate({
 *     year: 1989, month: 11, day: 15,
 *     hour: 14, minute: 30,
 *     lat: 48.8566, lng: 2.3522,
 *     unknownTime: false,
 *     utcOffset: 1
 *   });
 */

(function(global) {
  'use strict';

  var Ast = global.Astronomy;
  var DEG = Math.PI / 180;
  var RAD = 180 / Math.PI;

  // ─── Constants (unchanged) ──────────────────────────────────────
  var SIGNS = ['Aries','Taurus','Gemini','Cancer','Leo','Virgo',
               'Libra','Scorpio','Sagittarius','Capricorn','Aquarius','Pisces'];
  var SIGN_SYMBOLS = ['♈','♉','♊','♋','♌','♍','♎','♏','♐','♑','♒','♓'];

  // ─── Utility ──────────────────────────────────────────────────
  function normalize360(deg) {
    deg = deg % 360;
    return deg < 0 ? deg + 360 : deg;
  }

  function sinD(deg) { return Math.sin(deg * DEG); }
  function cosD(deg) { return Math.cos(deg * DEG); }
  function tanD(deg) { return Math.tan(deg * DEG); }
  function atan2D(y, x) { return normalize360(Math.atan2(y, x) * RAD); }

  // ─── Zodiac Conversion (unchanged) ────────────────────────────
  function toZodiac(longitude) {
    longitude = normalize360(longitude);
    var signIndex = Math.floor(longitude / 30);
    var degree = longitude - signIndex * 30;
    return {
      sign: SIGNS[signIndex],
      symbol: SIGN_SYMBOLS[signIndex],
      degree: Math.floor(degree),
      minutes: Math.floor((degree - Math.floor(degree)) * 60),
      longitude: longitude,
      signIndex: signIndex
    };
  }

  // ─── Planet Positions via Astronomy Engine ────────────────────
  // Returns geocentric ecliptic longitude in degrees (tropical)
  function getSunLongitude(date) {
    var pos = Ast.SunPosition(date);
    return normalize360(pos.elon);
  }

  function getMoonLongitude(date) {
    var pos = Ast.EclipticGeoMoon(date);
    return normalize360(pos.lon);
  }

  function getPlanetLongitude(body, date) {
    return normalize360(Ast.EclipticLongitude(body, date));
  }

  // ─── Mean North Node ─────────────────────────────────────────
  function getMeanNodeLongitude(date) {
    var T = date.ut / 36525.0;
    var lon = 125.0445479 - 1934.1362891 * T + 0.0020754 * T * T
              + T * T * T / 467441.0 - T * T * T * T / 60616000.0;
    return normalize360(lon);
  }

  // ─── Obliquity ────────────────────────────────────────────────
  function getObliquity(T) {
    // Meeus eq. 22.2 — high accuracy formula
    return 23.4392911 - 0.0130042 * T - 1.64e-7 * T * T + 5.04e-7 * T * T * T;
  }

  // ─── Ascendant Calculation ────────────────────────────────────
  // Uses Astronomy Engine's SiderealTime for GAST (more accurate than custom GMST)
  function calculateAscendant(date, lat, lng) {
    // Greenwich Apparent Sidereal Time in hours → convert to degrees
    var gastHours = Ast.SiderealTime(date);
    var GAST = gastHours * 15; // hours to degrees

    // Local Sidereal Time
    var LST = normalize360(GAST + lng);

    // Julian centuries for obliquity
    // AstroTime.ut = days since J2000
    var T = date.ut / 36525.0;
    var eps = getObliquity(T);

    // Ascendant formula (Meeus)
    var num = cosD(LST);
    var den = -(sinD(LST) * cosD(eps) + tanD(lat) * sinD(eps));
    return normalize360(atan2D(num, den));
  }

  // ─── MC (Midheaven) Calculation ───────────────────────────────
  function calculateMC(date, lng) {
    var gastHours = Ast.SiderealTime(date);
    var GAST = gastHours * 15;
    var LST = normalize360(GAST + lng);

    var T = date.ut / 36525.0;
    var eps = getObliquity(T);

    // MC = atan(tan(LST) / cos(eps))
    var mc = Math.atan2(sinD(LST), cosD(LST) * cosD(eps)) * RAD;
    return normalize360(mc);
  }

  // ─── Placidus House Cusps ─────────────────────────────────────
  // Calculates all 12 house cusps using the Placidus system.
  // Houses 1 (ASC), 4 (IC), 7 (DSC), 10 (MC) are exact.
  // Houses 2,3,5,6,8,9,11,12 use Placidus semi-arc interpolation.
  function calculatePlacidusHouses(date, lat, lng) {
    var ascLon = calculateAscendant(date, lat, lng);
    var mcLon = calculateMC(date, lng);
    var icLon = normalize360(mcLon + 180);
    var dscLon = normalize360(ascLon + 180);

    var T = date.ut / 36525.0;
    var eps = getObliquity(T);
    var gastHours = Ast.SiderealTime(date);
    var GAST = gastHours * 15;
    var RAMC = normalize360(GAST + lng); // same as LST in degrees

    var cusps = new Array(12);
    cusps[0] = ascLon;   // House 1 = ASC
    cusps[3] = icLon;    // House 4 = IC
    cusps[6] = dscLon;   // House 7 = DSC
    cusps[9] = mcLon;    // House 10 = MC

    // Placidus intermediate cusps via iterative method
    // For houses 11, 12 (above horizon, MC to ASC)
    // and houses 2, 3 (below horizon, IC to DSC)
    var latRad = lat * DEG;
    var epsRad = eps * DEG;

    // Semi-arc fractions for each cusp
    // House 11: 1/3 of diurnal semi-arc from MC
    // House 12: 2/3 of diurnal semi-arc from MC
    // House 2:  2/3 of nocturnal semi-arc from IC
    // House 3:  1/3 of nocturnal semi-arc from IC
    var cuspFractions = [
      { index: 10, f: 1/3 },  // House 11
      { index: 11, f: 2/3 },  // House 12
      { index: 1,  f: 1/3 },  // House 2
      { index: 2,  f: 2/3 }   // House 3
    ];

    for (var c = 0; c < cuspFractions.length; c++) {
      var cf = cuspFractions[c];
      cusps[cf.index] = placidusCusp(RAMC, lat, eps, cf.f, cf.index >= 10);
    }

    // Houses 5,6,8,9 are opposite houses 11,12,2,3
    cusps[4] = normalize360(cusps[10] + 180);  // House 5 opposite House 11
    cusps[5] = normalize360(cusps[11] + 180);  // House 6 opposite House 12
    cusps[7] = normalize360(cusps[1] + 180);   // House 8 opposite House 2
    cusps[8] = normalize360(cusps[2] + 180);   // House 9 opposite House 3

    return cusps;
  }

  // Iterative Placidus cusp calculation
  // fraction: 1/3 or 2/3 of semi-arc
  // isAbove: true for houses 11,12 (MC→ASC), false for 2,3 (IC→DSC)
  function placidusCusp(RAMC, lat, eps, fraction, isAbove) {
    var epsRad = eps * DEG;
    var latRad = lat * DEG;

    // Initial ARMC offset based on fraction
    var offset = isAbove ? (fraction * 90) : (fraction * 90 + 180);
    var cusp = normalize360(RAMC + offset);

    // Iterate to find the Placidus cusp (converges quickly)
    for (var iter = 0; iter < 50; iter++) {
      var prevCusp = cusp;

      // Declination of the ecliptic point at this longitude
      var cuspRad = cusp * DEG;
      var decl = Math.asin(Math.sin(epsRad) * Math.sin(cuspRad));

      // Semi-arc
      var tanDecl = Math.tan(decl);
      var tanLat = Math.tan(latRad);
      var ad = Math.asin(tanDecl * tanLat); // ascensional difference

      if (isNaN(ad) || !isFinite(ad)) {
        // Circumpolar — fall back to equal houses
        return normalize360(RAMC + offset);
      }

      var sa; // semi-arc in radians
      if (isAbove) {
        sa = (Math.PI / 2) + ad; // diurnal semi-arc
      } else {
        sa = (Math.PI / 2) - ad; // nocturnal semi-arc
      }

      // Oblique Ascension / Descension
      var oa;
      if (isAbove) {
        oa = RAMC * DEG + fraction * sa;
      } else {
        oa = RAMC * DEG + Math.PI + fraction * sa;
      }

      // Convert OA back to ecliptic longitude
      var oaDeg = normalize360(oa * RAD);
      cusp = oaToEclipticLon(oaDeg, eps, lat);

      if (Math.abs(cusp - prevCusp) < 0.001) break;
    }

    return normalize360(cusp);
  }

  // Convert Oblique Ascension to ecliptic longitude
  function oaToEclipticLon(oa, eps, lat) {
    var oaRad = oa * DEG;
    var epsRad = eps * DEG;
    var latRad = lat * DEG;

    // Ecliptic longitude from RAMC (right ascension of meridian cusp)
    var y = sinD(oa);
    var x = cosD(oa) * cosD(eps) + tanD(lat) * sinD(eps);

    // This is the reverse of the Ascendant formula
    // lon = atan2(sin(oa), cos(oa)*cos(eps) + tan(lat)*sin(eps))
    // but simplified for oblique ascension
    var num = sinD(oa);
    var den = cosD(oa) * cosD(eps) + tanD(0) * sinD(eps); // lat=0 for ecliptic conversion

    return normalize360(atan2D(Math.sin(oaRad), Math.cos(oaRad) * Math.cos(epsRad)));
  }

  // ─── Aspects Calculation (unchanged) ──────────────────────────
  var ASPECT_TYPES = [
    { name: 'conjunction', angle: 0, orb: 10, symbol: '☌', type: 'major' },
    { name: 'opposition', angle: 180, orb: 10, symbol: '☍', type: 'hard' },
    { name: 'trine', angle: 120, orb: 8, symbol: '△', type: 'soft' },
    { name: 'square', angle: 90, orb: 8, symbol: '□', type: 'hard' },
    { name: 'sextile', angle: 60, orb: 6, symbol: '⚹', type: 'soft' },
    { name: 'quincunx', angle: 150, orb: 3, symbol: '⚻', type: 'minor' },
    { name: 'semi-sextile', angle: 30, orb: 2, symbol: '⚺', type: 'minor' }
  ];

  function calculateAspects(planets) {
    var aspects = [];
    var keys = Object.keys(planets);
    for (var i = 0; i < keys.length; i++) {
      for (var j = i + 1; j < keys.length; j++) {
        var p1 = planets[keys[i]];
        var p2 = planets[keys[j]];
        var diff = Math.abs(p1.longitude - p2.longitude);
        if (diff > 180) diff = 360 - diff;

        for (var a = 0; a < ASPECT_TYPES.length; a++) {
          var asp = ASPECT_TYPES[a];
          var orbDiff = Math.abs(diff - asp.angle);
          if (orbDiff <= asp.orb) {
            aspects.push({
              planet1: keys[i],
              planet2: keys[j],
              aspect: asp.name,
              symbol: asp.symbol,
              type: asp.type,
              angle: asp.angle,
              orb: orbDiff,
              exact: orbDiff < 1
            });
            break;
          }
        }
      }
    }
    return aspects;
  }

  // ─── Rarity Calculation (unchanged) ───────────────────────────
  function calculateRarity(sunSign, moonSign, ascSign) {
    var base;
    if (ascSign) {
      base = 1 / 1728;
    } else {
      base = 1 / 144;
    }
    var hash = 0;
    var str = sunSign + moonSign + (ascSign || '');
    for (var i = 0; i < str.length; i++) {
      hash = ((hash << 5) - hash) + str.charCodeAt(i);
      hash |= 0;
    }
    var variation = (Math.abs(hash) % 40) / 100;
    var pct = (base * 100) + variation * 0.5;
    pct = Math.max(0.1, Math.min(0.5, pct));
    return Math.round(pct * 100) / 100;
  }

  // ─── Barnum Phrases (unchanged) ───────────────────────────────
  var BARNUM_PHRASES = {
    Aries: "Your chart reveals a rare fire that drives you to lead and inspire.",
    Taurus: "Your chart reveals a grounding energy that others find deeply comforting.",
    Gemini: "Your chart reveals a rare duality — two souls beautifully intertwined.",
    Cancer: "Your chart reveals an emotional depth that touches everyone around you.",
    Leo: "Your chart reveals a radiance that was born to light up the world.",
    Virgo: "Your chart reveals a quiet precision that the world desperately needs.",
    Libra: "Your chart reveals a rare harmony between head and heart.",
    Scorpio: "Your chart reveals an uncommon depth of emotional intensity.",
    Sagittarius: "Your chart reveals a restless soul destined for extraordinary paths.",
    Capricorn: "Your chart reveals a quiet strength that will move mountains.",
    Aquarius: "Your chart reveals a visionary mind ahead of its time.",
    Pisces: "Your chart reveals a soul connected to something beyond this world."
  };

  // ─── Main Calculation Function ────────────────────────────────
  // Same signature and return format as v1.0
  function calculate(opts) {
    var year = opts.year;
    var month = opts.month;
    var day = opts.day;
    var hour = opts.unknownTime ? 12 : (opts.hour || 12);
    var minute = opts.unknownTime ? 0 : (opts.minute || 0);
    var lat = opts.lat || 0;
    var lng = opts.lng || 0;
    var unknownTime = opts.unknownTime || false;
    var utcOffset = opts.utcOffset || 0;

    // Convert local time to UTC using the provided offset
    var utcDate;
    if (utcOffset !== 0 && !unknownTime) {
      var totalMinutes = hour * 60 + minute - utcOffset * 60;
      var dateObj = new Date(Date.UTC(year, month - 1, day));
      dateObj.setUTCMinutes(dateObj.getUTCMinutes() + totalMinutes);
      utcDate = dateObj;
    } else {
      utcDate = new Date(Date.UTC(year, month - 1, day, hour, minute, 0));
    }

    // Create Astronomy Engine date object
    var astroDate = new Ast.AstroTime(utcDate);

    // ─── Calculate all planetary positions ───────────────────
    var sunLon = getSunLongitude(astroDate);
    var moonLon = getMoonLongitude(astroDate);

    var planets = {
      Sun: toZodiac(sunLon),
      Moon: toZodiac(moonLon),
      Mercury: toZodiac(getPlanetLongitude('Mercury', astroDate)),
      Venus: toZodiac(getPlanetLongitude('Venus', astroDate)),
      Mars: toZodiac(getPlanetLongitude('Mars', astroDate)),
      Jupiter: toZodiac(getPlanetLongitude('Jupiter', astroDate)),
      Saturn: toZodiac(getPlanetLongitude('Saturn', astroDate)),
      Uranus: toZodiac(getPlanetLongitude('Uranus', astroDate)),
      Neptune: toZodiac(getPlanetLongitude('Neptune', astroDate)),
      Pluto: toZodiac(getPlanetLongitude('Pluto', astroDate)),
      Node: toZodiac(getMeanNodeLongitude(astroDate))
    };

    // ─── Ascendant & Houses ─────────────────────────────────
    var ascendant = null;
    var houseCusps = null;

    if (!unknownTime && lat !== 0) {
      var ascLon = calculateAscendant(astroDate, lat, lng);
      ascendant = toZodiac(ascLon);

      // Placidus house cusps
      try {
        houseCusps = calculatePlacidusHouses(astroDate, lat, lng);
      } catch (e) {
        // Fallback to equal houses for extreme latitudes
        houseCusps = [];
        for (var h = 0; h < 12; h++) {
          houseCusps.push(normalize360(ascLon + h * 30));
        }
      }
    }

    // ─── Aspects ────────────────────────────────────────────
    var aspectPlanets = {};
    for (var key in planets) {
      aspectPlanets[key] = planets[key];
    }
    if (ascendant) {
      aspectPlanets['Ascendant'] = ascendant;
    }
    if (houseCusps && houseCusps[9] !== undefined) {
      aspectPlanets['MC'] = toZodiac(houseCusps[9]);
    }
    var aspects = calculateAspects(aspectPlanets);

    // ─── Rarity & Barnum (unchanged) ────────────────────────
    var rarity = calculateRarity(
      planets.Sun.sign,
      planets.Moon.sign,
      ascendant ? ascendant.sign : null
    );
    var barnum = BARNUM_PHRASES[planets.Sun.sign] || BARNUM_PHRASES.Aries;

    // ─── Return (same structure + houseCusps) ───────────────
    return {
      planets: planets,
      ascendant: ascendant,
      houseCusps: houseCusps,
      aspects: aspects,
      rarity: rarity,
      barnum: barnum,
      sunSign: planets.Sun.sign,
      sunSymbol: planets.Sun.symbol,
      moonSign: planets.Moon.sign,
      moonSymbol: planets.Moon.symbol,
      ascSign: ascendant ? ascendant.sign : null,
      ascSymbol: ascendant ? ascendant.symbol : null,
      unknownTime: unknownTime
    };
  }

  // ─── Export (unchanged interface) ─────────────────────────────
  global.AstroCalc = {
    calculate: calculate,
    toZodiac: toZodiac,
    SIGNS: SIGNS,
    SIGN_SYMBOLS: SIGN_SYMBOLS
  };

})(typeof window !== 'undefined' ? window : this);
